<!--
  "Quem vai jogar?" — modal shown before recording starts.
  Captures names, colors, and initial side of the frame for each player.
  Emits a `MatchConfig` via a callback prop.
-->
<script lang="ts">
  import type { Player } from '@tbc/core';
  import type { MatchConfig } from './types.js';

  interface Props {
    onStart: (cfg: MatchConfig) => void;
    onSkip?: () => void;
  }

  const { onStart, onSkip }: Props = $props();

  const COLOR_SWATCHES = [
    '#22c55e', // neon green
    '#06b6d4', // cyan
    '#facc15', // racket yellow
    '#ec4899', // pink
    '#a855f7', // violet
    '#f97316', // orange
  ];

  let p1Name = $state('Jogador 1');
  let p1Color = $state('#22c55e');
  let p1Side = $state<'left' | 'right'>('left');

  let enableP2 = $state(false);
  let p2Name = $state('Jogador 2');
  let p2Color = $state('#06b6d4');
  let p2Side = $state<'right' | 'left'>('right');

  function confirm() {
    const p1: Player = {
      id: 'p1',
      name: p1Name.trim() || 'Jogador 1',
      color: p1Color,
      sideOfFrame: p1Side,
    };
    if (!enableP2) {
      onStart({ players: [p1], singlePlayer: true });
      return;
    }
    const p2: Player = {
      id: 'p2',
      name: p2Name.trim() || 'Jogador 2',
      color: p2Color,
      sideOfFrame: p2Side,
    };
    onStart({ players: [p1, p2], singlePlayer: false });
  }
</script>

<div class="backdrop" role="dialog" aria-modal="true" aria-labelledby="setup-title">
  <div class="card">
    <h2 id="setup-title">Quem vai jogar?</h2>

    <section class="player">
      <header class="ph">
        <span class="tag" style:background={p1Color}>P1</span>
        <input
          class="name-input"
          type="text"
          bind:value={p1Name}
          placeholder="Nome do jogador 1"
          aria-label="Nome do jogador 1"
        />
      </header>
      <div class="fields">
        <div class="field">
          <span class="label">Cor</span>
          <div class="swatches">
            {#each COLOR_SWATCHES as c}
              <button
                type="button"
                class="swatch"
                class:selected={p1Color === c}
                style:background={c}
                aria-label={`Cor ${c} para P1`}
                onclick={() => (p1Color = c)}
              ></button>
            {/each}
          </div>
        </div>
        <div class="field">
          <span class="label">Lado da câmera</span>
          <div class="side">
            <button type="button" class:active={p1Side === 'left'} onclick={() => (p1Side = 'left')}>Esquerdo</button>
            <button type="button" class:active={p1Side === 'right'} onclick={() => (p1Side = 'right')}>Direito</button>
          </div>
        </div>
      </div>
    </section>

    <label class="p2-toggle">
      <input type="checkbox" bind:checked={enableP2} />
      <span>Jogo em dupla / treino com parceiro</span>
    </label>

    {#if enableP2}
      <section class="player">
        <header class="ph">
          <span class="tag" style:background={p2Color}>P2</span>
          <input
            class="name-input"
            type="text"
            bind:value={p2Name}
            placeholder="Nome do jogador 2"
            aria-label="Nome do jogador 2"
          />
        </header>
        <div class="fields">
          <div class="field">
            <span class="label">Cor</span>
            <div class="swatches">
              {#each COLOR_SWATCHES as c}
                <button
                  type="button"
                  class="swatch"
                  class:selected={p2Color === c}
                  style:background={c}
                  aria-label={`Cor ${c} para P2`}
                  onclick={() => (p2Color = c)}
                ></button>
              {/each}
            </div>
          </div>
          <div class="field">
            <span class="label">Lado da câmera</span>
            <div class="side">
              <button type="button" class:active={p2Side === 'left'} onclick={() => (p2Side = 'left')}>Esquerdo</button>
              <button type="button" class:active={p2Side === 'right'} onclick={() => (p2Side = 'right')}>Direito</button>
            </div>
          </div>
        </div>
      </section>
    {/if}

    <footer class="actions">
      {#if onSkip}
        <button type="button" class="ghost" onclick={onSkip}>Pular</button>
      {/if}
      <button type="button" class="primary" onclick={confirm}>Confirmar</button>
    </footer>
  </div>
</div>

<style>
  .backdrop {
    position: absolute;
    inset: 0;
    background: rgba(2, 6, 23, 0.8);
    backdrop-filter: blur(6px);
    display: grid;
    place-items: center;
    z-index: 40;
    padding: 16px;
  }
  .card {
    width: min(520px, 100%);
    background: #0f172a;
    border: 1px solid rgba(148, 163, 184, 0.2);
    border-radius: 16px;
    padding: 20px;
    color: #e2e8f0;
    display: flex;
    flex-direction: column;
    gap: 14px;
    font-family: system-ui, sans-serif;
  }
  h2 { font-size: 20px; font-weight: 700; margin: 0; }

  .player {
    background: #1e293b;
    border-radius: 10px;
    padding: 12px;
    display: flex;
    flex-direction: column;
    gap: 10px;
  }
  .ph { display: flex; align-items: center; gap: 10px; }
  .tag {
    font-size: 11px;
    font-weight: 700;
    color: #0f172a;
    padding: 4px 8px;
    border-radius: 6px;
    font-family: 'JetBrains Mono', ui-monospace, monospace;
  }
  .name-input {
    flex: 1;
    background: #0f172a;
    border: 1px solid rgba(148, 163, 184, 0.2);
    border-radius: 6px;
    padding: 8px 10px;
    color: #e2e8f0;
    font-family: inherit;
    font-size: 14px;
  }

  .fields { display: flex; flex-direction: column; gap: 10px; }
  .field { display: flex; flex-direction: column; gap: 6px; }
  .label { font-size: 11px; color: #94a3b8; letter-spacing: 0.08em; text-transform: uppercase; }

  .swatches { display: flex; gap: 6px; flex-wrap: wrap; }
  .swatch {
    width: 28px; height: 28px; border-radius: 6px;
    border: 2px solid transparent; cursor: pointer;
  }
  .swatch.selected { border-color: #f8fafc; box-shadow: 0 0 0 2px #0f172a, 0 0 0 4px currentColor; }

  .side { display: flex; gap: 6px; }
  .side button {
    padding: 6px 12px;
    background: #0f172a;
    color: #cbd5e1;
    border: 1px solid rgba(148, 163, 184, 0.2);
    border-radius: 6px;
    cursor: pointer;
    font-family: inherit;
    font-size: 13px;
  }
  .side button.active { background: #22c55e; color: #0f172a; border-color: #22c55e; font-weight: 600; }

  .p2-toggle {
    display: flex;
    align-items: center;
    gap: 8px;
    font-size: 14px;
    color: #cbd5e1;
    cursor: pointer;
  }

  .actions { display: flex; justify-content: flex-end; gap: 8px; }
  .primary {
    background: #22c55e;
    color: #0f172a;
    border: none;
    border-radius: 8px;
    padding: 10px 20px;
    font-weight: 700;
    cursor: pointer;
    font-family: inherit;
    font-size: 14px;
  }
  .ghost {
    background: transparent;
    color: #cbd5e1;
    border: 1px solid rgba(148, 163, 184, 0.25);
    border-radius: 8px;
    padding: 10px 16px;
    cursor: pointer;
    font-family: inherit;
    font-size: 14px;
  }
</style>
